/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 10/09/2025 14:27
 *
 */

import {
  computed,
  Directive,
  EventEmitter,
  input,
  OnChanges,
  Output,
  Signal,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { ActionEvent, StandardAction } from '@infodata-group/inf-commons';

/**
 * @Deprecated:: Only for dev debug purpose, use StandardInput from @infodata-group/inf-commons-mobile instead
 */
@Directive()
export abstract class StandardInput<T> implements OnChanges {
  public label = input<string>('');
  public icon = input<string>('');
  protected _icon = signal<string>('');
  public name = input<string>('');
  public actions = input<StandardAction[]>([]);
  public enable = input<boolean>(true);

  public defaultValue = input<T>();
  public placeholder = input<string>('');
  public displayedPlaceholder = computed(() => {
    if (!this.isFocus()) {
      return '';
    }
    if (!this.placeholder()) {
      return '';
    }
    return this.placeholder();
  });
  public error = input<string>('');
  protected _error = signal<string>('');
  public warning = input<string>('');
  protected _warning = signal<string>('');
  public isMandatory = input<boolean>(false);

  public displayIcon = input<boolean>(true);
  public isFocus = signal<boolean>(false);
  public abstract currentValue: WritableSignal<T>;

  protected lastValueEmitted: WritableSignal<T>;

  protected abstract additionalActions: Signal<StandardAction[]>;
  protected abstract isFilled: Signal<boolean>;
  protected displayedLabel = computed(() => {
    const isMandatory = this.isMandatory() ? ' *' : '';
    if (this.label()) {
      return `${this.label()}${isMandatory}`;
    }
    if (this.placeholder()) {
      return `${this.placeholder()}${isMandatory}`;
    }
    return '';
  });
  protected displayedIcon = computed(() => {
    if (!this.displayIcon()) {
      return '';
    }
    if (this.icon()) {
      return this.icon();
    }
    return this._icon();
  });
  protected displayedActions = computed(() => {
    return [...this.actions(), ...this.additionalActions()];
  });
  protected displayedError = computed(() => {
    if (this.error()) {
      return this.error();
    }
    if (this._error()) {
      return this._error();
    }
    return '';
  });
  protected displayedWarning = computed(() => {
    if (this.warning()) {
      return this.warning();
    }
    if (this._warning()) {
      return this._warning();
    }
    return '';
  });

  // Todo:: implemente that on all inputs
  protected lastValueSended: T;

  @Output() update = new EventEmitter<T>();
  @Output() actionClick = new EventEmitter<ActionEvent>();

  ngOnChanges(changes: SimpleChanges) {
    if (Object.hasOwn(changes, 'defaultValue') && this.defaultValue()) {
      this.currentValue.set(this.defaultValue());
      this.checkValue();
      this.sendUpdate();
    }
  }

  protected onClickAction($event: StandardAction) {
    switch ($event.name) {
      case 'clear':
        this.clear();
        break;
      default:
        this.actionClick.emit(new ActionEvent('click', $event));
        break;
    }
  }

  public abstract clear(): void;
  public abstract focus(): void;

  protected checkValue(): void {
    this._error.set('');
    this._warning.set('');
    this.checkMandatory();
  }
  protected checkMandatory(): void {
    if (this.isMandatory() && !this.currentValue()) {
      this._error.set('i18n.form.mandatory');
    }
  }

  public getValue(): T {
    return this.currentValue();
  }

  public setError(error: string): void {
    this._error.set(error);
  }

  protected sendUpdate() {
    if (this.currentValue() === this.lastValueSended) {
      return;
    }
    this.lastValueSended = this.currentValue();
    this.update.emit(this.currentValue());
  }
}
